import type { TimeEntry, ISODateString, BankHoliday, PeriodMonth } from '../types/timesheet.types';
import { ProjectType } from '../types/timesheet.types';
import { BANK_HOLIDAY_PROJECT_CODE } from './constants';
import type { WeekSummary } from '../types/approval.types';

type ProjectTypeMap = ReadonlyMap<string, { type: ProjectType }>;

/** Returns the ISO Monday (week start) for a given date. */
function getWeekStart(dateStr: ISODateString): ISODateString {
  const d = new Date(dateStr + 'T00:00:00Z');
  const dow = d.getUTCDay(); // 0=Sun
  const shift = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + shift);
  return d.toISOString().slice(0, 10) as ISODateString;
}

function formatWeekLabel(mondayIso: ISODateString): string {
  const d = new Date(mondayIso + 'T00:00:00Z');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `W/C ${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
}

/**
 * Groups flat TimeEntry[] by ISO week and returns an ordered WeekSummary[] (W1, W2…).
 * All entries must belong to a single person-month; grouping is by Monday-aligned week.
 * bankHolidays is optional — when supplied, bhHours is computed per week (deduplicated by date
 * so SP's multi-region rows for the same date are counted only once).
 */
export function buildWeeklySummaries(
  entries: readonly TimeEntry[],
  projectTypeMap: ProjectTypeMap,
  bankHolidays: readonly BankHoliday[] = [],
  periodMonth?: PeriodMonth
): WeekSummary[] {
  const byWeek = new Map<ISODateString, TimeEntry[]>();
  for (const entry of entries) {
    const ws = getWeekStart(entry.dateWorked);
    const bucket = byWeek.get(ws);
    if (bucket) bucket.push(entry);
    else byWeek.set(ws, [entry]);
  }

  // Deduplicate BH by calendar date (SP can have UK + All rows for the same date)
  const bhByDate = new Map<ISODateString, number>();
  for (const bh of bankHolidays) {
    if (!bhByDate.has(bh.holidayDate)) bhByDate.set(bh.holidayDate, bh.defaultHours);
  }

  // A week that contains a bank holiday but has no submitted TimeEntry rows (e.g. the
  // person did no other work that week) would otherwise be silently dropped — add an
  // empty bucket for it so it still renders as a week tile and counts its BH hours.
  if (periodMonth) {
    for (const bhDate of bhByDate.keys()) {
      if (bhDate.slice(0, 7) !== periodMonth) continue;
      const ws = getWeekStart(bhDate);
      if (!byWeek.has(ws)) byWeek.set(ws, []);
    }
  }

  const fallbackStatus = entries[0]?.status;
  const sortedStarts = [...byWeek.keys()].sort();

  return sortedStarts.map((weekStart, idx) => {
    const we = byWeek.get(weekStart)!;
    const breakdown: Record<ProjectType, number> = {
      [ProjectType.RD]: 0,
      [ProjectType.GA]: 0,
      [ProjectType.Leave]: 0,
      [ProjectType.Commercial]: 0,
      [ProjectType.Standard]: 0,
    };
    for (const e of we) {
      const pt = projectTypeMap.get(e.projectCode)?.type ?? ProjectType.Standard;
      breakdown[pt] += e.hours;
    }

    // Sum BH hours only for BH days where the person did NOT enter project work.
    // If they worked on a BH day, project hours are already in totalHours; adding
    // calendar BH hours on top would double-count.
    const bhHours = getWeekDays(weekStart).reduce((s, d) => {
      if (!bhByDate.has(d)) return s;
      const hasProjectHours = we.some(
        e => e.dateWorked === d && e.projectCode !== BANK_HOLIDAY_PROJECT_CODE && e.hours > 0
      );
      return hasProjectHours ? s : s + (bhByDate.get(d) ?? 0);
    }, 0);

    return {
      weekNumber: `W${idx + 1}`,
      weekStart,
      label: formatWeekLabel(weekStart),
      totalHours: we.filter(e => e.projectCode !== BANK_HOLIDAY_PROJECT_CODE).reduce((s, e) => s + e.hours, 0),
      bhHours,
      breakdownByType: breakdown,
      status: we[0]?.status ?? fallbackStatus,
      entries: we,
    };
  });
}

/** Returns Mon–Sun ISO dates for a week given its Monday start. */
export function getWeekDays(weekStart: ISODateString): ISODateString[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + i);
    return d.toISOString().slice(0, 10) as ISODateString;
  });
}
